<template>
  <div
    class="bg-action-default-mid text-action-default-hi rounded-full flex w-full flex-row-reverse items-center gap-4 pl-4 pr-8 focus-within:!bg-action-default-mid hover:bg-action-default-mid-hover"
  >
    <button
      v-show="value"
      :aria-label="i18n(translations.resetSearchButtonAriaLabel)"
      class="text-action-default-hi focus-visible-outline-inset-hi rounded-sm h-32 w-32 cursor-pointer items-center justify-center transition-colors duration-100 hover:text-action-default-hi-hover"
      type="reset"
      @click="resetHandler"
    >
      ✕
    </button>
    <input
      :id="inputId"
      ref="input"
      v-focus="isFocus"
      aria-describedby="resultsListBoxAlert"
      :aria-expanded="isListboxOpen"
      autocomplete="off"
      autocorrect="off"
      class="text-action-default-hi peer h-[40px] w-full appearance-none text-ellipsis bg-transparent outline-none"
      :class="
        hasImproveSearchUIExperiment
          ? 'placeholder:text-action-default-hi md:h-[44px] label-large'
          : 'placeholder:text-action-default-low body-1'
      "
      data-qa="search-bar-input"
      :name="inputName"
      :placeholder="i18n(translations.textboxPlaceholder)"
      role="searchbox"
      type="text"
      :value
      @focus="handleFocus"
      @input="changeHandler"
      @keydown.down.prevent="specialKeyHandler"
      @keydown.enter.prevent="submitHandler"
      @keydown.esc.prevent="resetHandler"
      @keydown.up.prevent="specialKeyHandler"
    />
    <button
      :aria-label="i18n(translations.searchButtonAriaLabel)"
      class="text-action-default-hi focus-visible-outline-inset-hi rounded-sm flex h-32 w-32 shrink-0 cursor-pointer items-center justify-center transition-colors duration-100 hover:text-action-default-hi-hover"
      type="submit"
      @click="submitHandler"
    >
      <IconSearch :size="hasImproveSearchUIExperiment ? '20' : '24'" />
    </button>
    <div id="resultsListBoxAlert" class="sr-only">
      <p role="alert">{{ listboxResultsMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { vFocus } from '@ds/directives/focus'
import { IconSearch } from '@ds/icons/IconSearch'

import translations from './Textbox.translations'

const i18n = useI18n()

const emit = defineEmits(['change', 'submit', 'keydown', 'focus'])

const props = withDefaults(
  defineProps<{
    isFocus?: boolean
    value: string | null
    inputName?: string
    inputId: string
    isListboxOpen: boolean
    listboxResultsMessage: string
  }>(),
  {
    isFocus: false,
    inputName: 'q',
  },
)

const input = ref<HTMLInputElement | null>(null)

const experiments = useExperiments()

const hasImproveSearchUIExperiment =
  experiments['experiment.improveSearchUi'] === 'new'

function submitHandler() {
  emit('submit', props.value)
}

function changeHandler(e: Event) {
  const { target } = e
  const { value } = target as HTMLInputElement
  emit('change', value)
}

function specialKeyHandler({ code }: { code: string }) {
  if (code === 'ArrowUp') {
    emit('keydown', -1)
  } else if (code === 'ArrowDown') {
    emit('keydown', 1)
  }
}

function handleFocus() {
  input.value?.focus()
  const valueLength = props.value?.length ?? 0
  input.value?.setSelectionRange(valueLength, valueLength)
  emit('focus')
}

function resetHandler() {
  emit('change', '')
  handleFocus()
}

function hydrated() {
  if (input.value === document.activeElement) {
    handleFocus()
  }
}

defineExpose({
  handleFocus,
  input,
})

onMounted(() => {
  hydrated()
})
</script>
